<template>
  <div
      class="app-grid__row"
      :class="classes"
      :style="{
        minHeight: `${ typeof minHeight === 'number' ? (`${minHeight}px`) : minHeight }`
      }"
  >
    <Component
        v-for="(colItem, inx) in colItems"
        :key="`col-item-${inx}`"
        :is="colItem"
    />
  </div>
</template>

<script lang="ts" setup>
import { onBreakpointChanged, removeOnBreakpointChanged, getCurrentBreakpoints } from '@/utils/viewport';
import {
  computed, provide,
  ref, RendererNode, onBeforeUnmount, useSlots, VNode, onBeforeMount, watch,
} from 'vue';

const props = defineProps<{
  start?: boolean
  center?: boolean
  end?: boolean
  verticalStart?: boolean
  verticalCenter?: boolean
  verticalEnd?: boolean
  minHeight?: number | string
  noGap?: boolean
  defaultColSize?: Record<string, number> | number | string,
  small?: boolean
  large?: boolean
}>();

provide('defaultColSize', props.defaultColSize);

const classes = computed(() => {
  const classesArr = [];

  if (props.center) {
    classesArr.push('justify-content-center');
  } else if (props.end) {
    classesArr.push('justify-content-end');
  } else if (props.start) {
    classesArr.push('justify-content-start');
  }

  if (props.verticalCenter) {
    classesArr.push('align-items-center');
  } else if (props.verticalEnd) {
    classesArr.push('align-items-end');
  } else if (props.verticalStart) {
    classesArr.push('align-items-start');
  }

  if (props.noGap) {
    classesArr.push('no-gap');
  }

  if (props.small) {
    classesArr.push('small-gap');
  } else if (props.large) {
    classesArr.push('large-gap');
  }

  return classesArr;
});

const slots = useSlots();
const slotDefault = computed(() => (slots.default ? slots.default() : null));
const colItems = ref<VNode<RendererNode>[]>([]);

const setColItems = (event: CustomEvent) => {
  const breakpoints = (event.detail?.breakpoints || ['xs']) as string[];

  colItems.value = [...(slotDefault.value || [])].map((item, inx) => {
    const mappedItem = { ...item };
    let order = inx;

    if (typeof item.props?.order === 'object') {
      let orderByBp: undefined | number;
      const propBps = mappedItem.props?.order;
      breakpoints.forEach((bpKey) => {
        if (Object.keys(propBps).includes(bpKey)) {
          if (propBps[bpKey] !== undefined) {
            orderByBp = propBps[bpKey];
          }
        }
      });

      order = typeof orderByBp === 'number' ? orderByBp : order;
    }

    mappedItem.props = {
      ...item.props,
      order,
    };

    return mappedItem;
  });
};

watch(() => slotDefault.value, () => {
  setColItems({
    detail: {
      breakpoints: getCurrentBreakpoints(),
    },
  } as unknown as CustomEvent);
});

onBeforeMount(() => {
  onBreakpointChanged(setColItems, true);
});

onBeforeUnmount(() => {
  removeOnBreakpointChanged(setColItems);
});
</script>

<style lang="scss">
@import "src/scss/component-helpers";

.app-grid__row {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 20px;

  @media (min-width: map_get($grid-breakpoints, 'lg')) {
    gap: 30px;
  }

  &.small-gap {
    gap: 10px;

    @media (min-width: map_get($grid-breakpoints, 'lg')) {
      gap: 20px;
    }
  }

  &.large-gap {
    gap: 30px;

    @media (min-width: map_get($grid-breakpoints, 'lg')) {
      gap: 40px;
    }
  }

  &.no-gap {
    gap: 0 !important;
  }
}
</style>
